import { LayerExtension, TextureSource } from "@deck.gl/core"
import { BitmapLayer, BitmapLayerProps } from "@deck.gl/layers"
import { TileLayerProps } from "@deck.gl/geo-layers"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { CogTileSource, fetchCogTile } from "./fetchCogTile"
import { TrackedTileLayer } from "./TrackedTileLayer"

export type CogTileExtent = [minLng: number, minLat: number, maxLng: number, maxLat: number]

export type CogTileBounds = [west: number, south: number, east: number, north: number]

export type CogTile = {
    index: { x: number; y: number; z: number }
    bounds: CogTileBounds
    data: ImageData | null
}

export type CogTileLayerOptions<TBitmapProps extends object = object> = {
    id: string
    source: CogTileSource
    extent?: CogTileExtent
    minZoom?: number
    maxZoom?: number
    tileSize?: 256 | 512
    maxRequests?: number
    refinementStrategy?: TileLayerProps["refinementStrategy"]
    additionalExtensions?: LayerExtension[]
    transform?: (data: ImageData) => ImageData
    bitmap?: (tile: CogTile) => TBitmapProps & Partial<BitmapLayerProps>
    invalidateOn?: { source?: unknown[]; bitmap?: unknown[] }
}

const TILE_GRID_SIZE = 256
const TILE_Z_RANGE: [number, number] = [0, 2000]

export const CogTileLayer = <TBitmapProps extends object = object>({
    id,
    source,
    extent,
    minZoom,
    maxZoom,
    tileSize = 256,
    maxRequests,
    refinementStrategy,
    additionalExtensions = [],
    transform,
    bitmap,
    invalidateOn,
}: CogTileLayerOptions<TBitmapProps>) =>
    new TrackedTileLayer({
        id,
        extent,
        minZoom,
        maxZoom,
        maxRequests,
        refinementStrategy,
        tileSize: TILE_GRID_SIZE,
        zRange: TILE_Z_RANGE,
        debounceTime: 100,
        updateTriggers: {
            getTileData: invalidateOn?.source ?? [],
            renderSubLayers: invalidateOn?.bitmap ?? [],
        },
        getTileData: ({ index, signal }) => fetchCogTile(source, { ...index, tileSize }, signal),
        renderSubLayers: (props) => {
            const { bbox } = props.tile
            if (!("west" in bbox)) throw new Error("CogTileLayer: expected a geo bbox")

            const bounds: CogTileBounds = [bbox.west, bbox.south, bbox.east, bbox.north]
            const raw = (props.tile.data ?? null) as ImageData | null
            const data = raw && transform ? transform(raw) : raw
            const tile: CogTile = { index: props.tile.index, bounds, data }

            return new BitmapLayer({
                ...bitmap?.(tile),
                id: `${props.id}-bitmap`,
                image: data as TextureSource,
                bounds,
                extensions: [...getGlobalTerrainData().extensions, ...additionalExtensions],
            })
        },
    })
